import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Edit2, Trash2, Eye, ArrowUp, ArrowDown, ExternalLink, Save, X, Image as ImageIcon, Check,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const slugify = (s = "") =>
  s.toLowerCase().replace(/[^a-z0-9\s-]/g, "").trim().replace(/\s+/g, "-").slice(0, 80);

const abs = (url) => (!url ? "" : url.startsWith("http") ? url : url.startsWith("/api/") ? API.replace(/\/api$/, "") + url : url);

const EMPTY = {
  title: "", slug: "", excerpt: "", body_md: "", cover: "", author: "House of Barique",
  tags: [], read_minutes: 5, is_published: true,
};

// Same lightweight Markdown -> HTML used on the storefront (kept in sync so admin
// preview matches the published article).
function renderMd(md) {
  if (!md) return "";
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const lines = md.split("\n");
  const out = []; let para = [];
  const flushPara = () => { const t = para.join(" ").trim(); if (t) out.push(`<p>${t}</p>`); para = []; };
  const inl = (r) => {
    let t = esc(r);
    t = t.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    t = t.replace(/\*([^*]+)\*/g, "<em>$1</em>");
    t = t.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer">$1</a>');
    return t;
  };
  for (const line of lines) {
    if (line.startsWith("## ")) { flushPara(); out.push(`<h2>${inl(line.slice(3).trim())}</h2>`); }
    else if (line.startsWith("# ")) { flushPara(); out.push(`<h1>${inl(line.slice(2).trim())}</h1>`); }
    else if (line.trim() === "") { flushPara(); }
    else { para.push(inl(line)); }
  }
  flushPara();
  return out.join("\n");
}

export default function AdminJournal() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [media, setMedia] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [editing, setEditing] = useState(null); // null | {} | article
  const [previewTab, setPreviewTab] = useState("write"); // 'write' | 'preview'
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/articles");
      setList(data || []);
    } catch { setList([]); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    if (user?.role !== "admin") return;
    load();
    api.get("/media").then((r) => setMedia(r.data || [])).catch(() => {});
  }, [user]);

  const sorted = useMemo(
    () => [...list].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [list]
  );

  if (user === undefined) return <div className="pt-40 container-lux">Loading…</div>;
  if (!user) return <Navigate to="/login?next=/admin/journal" replace />;
  if (user.role !== "admin") return (
    <div className="pt-40 container-lux"><p className="font-serif text-3xl">Reserved for the atelier.</p></div>
  );

  const openNew = () => { setEditing({ ...EMPTY }); setPreviewTab("write"); };
  const openEdit = (a) => { setEditing({ ...a }); setPreviewTab("write"); };
  const close = () => setEditing(null);
  const set = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

  const save = async () => {
    if (!editing.title?.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const body = {
        title: editing.title.trim(),
        slug: editing.slug?.trim() || slugify(editing.title),
        excerpt: editing.excerpt || "",
        body_md: editing.body_md || "",
        cover: editing.cover || "",
        author: editing.author || "House of Barique",
        tags: Array.isArray(editing.tags) ? editing.tags : String(editing.tags || "").split(",").map((s) => s.trim()).filter(Boolean),
        read_minutes: Number(editing.read_minutes) || 5,
        is_published: editing.is_published !== false,
      };
      if (editing.id) await api.put(`/admin/articles/${editing.id}`, body);
      else await api.post("/admin/articles", body);
      toast.success("Story saved");
      close();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"? This cannot be undone.`)) return;
    try { await api.delete(`/admin/articles/${a.id}`); toast.success("Story removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const move = async (a, dir) => {
    const sorted = [...list].sort((x, y) => (x.order ?? 0) - (y.order ?? 0));
    const idx = sorted.findIndex((x) => x.id === a.id);
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const b = sorted[swapIdx];
    try {
      await Promise.all([
        api.patch(`/admin/articles/${a.id}/order`, { order: b.order ?? swapIdx }),
        api.patch(`/admin/articles/${b.id}/order`, { order: a.order ?? idx }),
      ]);
      load();
    } catch { toast.error("Could not reorder"); }
  };

  const togglePublished = async (a) => {
    try {
      await api.put(`/admin/articles/${a.id}`, {
        title: a.title, slug: a.slug, excerpt: a.excerpt || "",
        body_md: a.body_md || "", cover: a.cover || "",
        author: a.author || "House of Barique",
        tags: Array.isArray(a.tags) ? a.tags : [],
        read_minutes: a.read_minutes || 5,
        is_published: !a.is_published,
      });
      toast.success(a.is_published ? "Unpublished" : "Published");
      load();
    } catch { toast.error("Update failed"); }
  };

  return (
    <div className="pt-32 pb-24 bg-ivory min-h-screen" data-testid="admin-journal">
      <div className="container-lux">
        <div className="flex items-start justify-between gap-8 flex-wrap">
          <div>
            <p className="overline text-muted-foreground">The Editorial</p>
            <h1 className="mt-4 font-serif tracking-tighter text-5xl md:text-6xl font-light">Journal CMS</h1>
            <p className="mt-4 text-sm text-muted-foreground max-w-2xl">
              Publish long-form stories about the atelier. Everything shows up on <Link to="/journal" className="underline">/journal</Link> and in the sitemap the moment you save.
            </p>
          </div>
          <div className="flex gap-3">
            <Link to="/admin" className="lux-btn-outline py-3 px-5 text-[10px]" data-testid="back-dash">← Dashboard</Link>
            <button onClick={openNew} className="lux-btn-dark py-3 px-5 text-[10px] flex items-center gap-2" data-testid="new-article-btn">
              <Plus size={13} strokeWidth={1.5} /> New Story
            </button>
          </div>
        </div>

        <div className="divider-thin my-10" />

        {loading ? (
          <p className="overline text-muted-foreground/60">Loading…</p>
        ) : sorted.length === 0 ? (
          <div className="text-center py-24">
            <p className="font-serif text-3xl">No stories yet</p>
            <button onClick={openNew} className="mt-6 lux-btn-dark py-3 px-6 text-[10px]">Write the first</button>
          </div>
        ) : (
          <div className="border border-border">
            <div className="hidden md:grid grid-cols-[80px_1fr_180px_140px_120px_220px] gap-4 px-4 py-3 border-b border-border bg-muted/40 overline text-muted-foreground">
              <span>Cover</span><span>Title</span><span>Author</span><span>Read</span><span>Status</span><span className="text-right">Actions</span>
            </div>
            <ul>
              {sorted.map((a, i) => (
                <li key={a.id} data-testid={`row-${a.id}`} className="grid md:grid-cols-[80px_1fr_180px_140px_120px_220px] gap-4 items-center px-4 py-4 border-b border-border/60 last:border-b-0">
                  <div className="w-16 h-16 bg-muted overflow-hidden">
                    {a.cover ? <img src={abs(a.cover)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-muted-foreground"><ImageIcon size={16} strokeWidth={1} /></div>}
                  </div>
                  <div>
                    <p className="font-serif text-lg leading-tight">{a.title}</p>
                    <p className="text-xs text-muted-foreground mt-1">/journal/{a.slug}</p>
                  </div>
                  <p className="text-sm">{a.author || "—"}</p>
                  <p className="text-sm">{a.read_minutes ? `${a.read_minutes} min` : "—"}</p>
                  <button
                    onClick={() => togglePublished(a)}
                    data-testid={`toggle-pub-${a.id}`}
                    className={`text-[10px] uppercase tracking-widest px-3 py-1 border w-fit ${a.is_published ? "bg-ink text-ivory border-ink" : "border-border text-muted-foreground"}`}
                  >{a.is_published ? "Published" : "Draft"}</button>
                  <div className="flex gap-2 justify-end flex-wrap">
                    <button onClick={() => move(a, -1)} disabled={i === 0} data-testid={`up-${a.id}`} className="p-2 border border-border hover:border-ink disabled:opacity-30 disabled:hover:border-border"><ArrowUp size={13} strokeWidth={1.5} /></button>
                    <button onClick={() => move(a, +1)} disabled={i === sorted.length - 1} data-testid={`down-${a.id}`} className="p-2 border border-border hover:border-ink disabled:opacity-30 disabled:hover:border-border"><ArrowDown size={13} strokeWidth={1.5} /></button>
                    <a href={`/journal/${a.slug}`} target="_blank" rel="noreferrer" data-testid={`view-${a.id}`} className="p-2 border border-border hover:border-ink"><Eye size={13} strokeWidth={1.5} /></a>
                    <button onClick={() => openEdit(a)} data-testid={`edit-${a.id}`} className="p-2 border border-border hover:border-ink"><Edit2 size={13} strokeWidth={1.5} /></button>
                    <button onClick={() => remove(a)} data-testid={`del-${a.id}`} className="p-2 border border-border hover:border-destructive hover:text-destructive"><Trash2 size={13} strokeWidth={1.5} /></button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && close()}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto bg-ivory border border-border rounded-none p-0">
          {editing && (
            <div className="p-8 md:p-10">
              <div className="flex justify-between items-start gap-6">
                <div>
                  <p className="overline text-muted-foreground">{editing.id ? "Edit story" : "New story"}</p>
                  <DialogHeader>
                    <DialogTitle className="font-serif text-3xl md:text-4xl tracking-tighter font-light mt-2">
                      {editing.title || "Untitled story"}
                    </DialogTitle>
                  </DialogHeader>
                </div>
                <button onClick={close} className="p-2 hover:bg-muted"><X size={16} strokeWidth={1} /></button>
              </div>

              <div className="divider-thin my-8" />

              <div className="grid md:grid-cols-2 gap-6">
                <Field label="Title" testid="a-title" value={editing.title} onChange={(e) => set("title", e.target.value)} onBlur={() => { if (!editing.slug) set("slug", slugify(editing.title)); }} />
                <Field label="Slug" testid="a-slug" value={editing.slug} onChange={(e) => set("slug", slugify(e.target.value))} />
                <Field label="Author" testid="a-author" value={editing.author} onChange={(e) => set("author", e.target.value)} />
                <Field label="Read time (min)" testid="a-read" type="number" min="1" max="60" value={editing.read_minutes} onChange={(e) => set("read_minutes", e.target.value)} />
                <Field label="Tags (comma separated)" testid="a-tags" value={Array.isArray(editing.tags) ? editing.tags.join(", ") : editing.tags} onChange={(e) => set("tags", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} placeholder="Atelier, Craft, Heritage" />
                <div className="flex items-end gap-4">
                  <label className="flex items-center gap-3 cursor-pointer" data-testid="a-published">
                    <input type="checkbox" checked={editing.is_published !== false} onChange={(e) => set("is_published", e.target.checked)} className="w-4 h-4 accent-ink" />
                    <span className="overline">{editing.is_published !== false ? "Published" : "Draft"}</span>
                  </label>
                </div>
              </div>

              <div className="mt-6">
                <label className="overline text-muted-foreground">Cover Image</label>
                <div className="mt-3 grid md:grid-cols-[160px_1fr_auto] gap-4 items-center">
                  <div className="w-40 h-40 bg-muted overflow-hidden border border-border">
                    {editing.cover ? <img src={abs(editing.cover)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-muted-foreground"><ImageIcon size={20} strokeWidth={1} /></div>}
                  </div>
                  <input className="lux-input" data-testid="a-cover" value={editing.cover} onChange={(e) => set("cover", e.target.value)} placeholder="Paste image URL or pick from library →" />
                  <button type="button" onClick={() => setShowPicker(true)} className="lux-btn-outline py-3 px-5 text-[10px]" data-testid="pick-cover">Pick from Library</button>
                </div>
              </div>

              <div className="mt-6">
                <Field label="Excerpt · 1-2 sentences" testid="a-excerpt" value={editing.excerpt} onChange={(e) => set("excerpt", e.target.value)} placeholder="A single line that will tempt the reader in." />
              </div>

              <div className="mt-8">
                <div className="flex items-center justify-between border-b border-border mb-4">
                  <div className="flex">
                    {["write", "preview"].map((t) => (
                      <button key={t} onClick={() => setPreviewTab(t)} className={`px-5 py-3 text-[11px] uppercase tracking-widest border-b-2 -mb-px ${previewTab === t ? "border-ink text-ink" : "border-transparent text-muted-foreground hover:text-ink"}`} data-testid={`tab-${t}`}>
                        {t === "write" ? "Write" : "Preview"}
                      </button>
                    ))}
                  </div>
                  <span className="overline text-muted-foreground/60">Markdown · # H1, ## H2, **bold**, *italic*, [link](url)</span>
                </div>
                {previewTab === "write" ? (
                  <textarea
                    data-testid="a-body"
                    rows={20}
                    value={editing.body_md}
                    onChange={(e) => set("body_md", e.target.value)}
                    className="w-full bg-transparent border border-border p-4 text-sm leading-relaxed focus:outline-none focus:border-ink font-mono"
                    placeholder={"# Title\n\n## Section\n\nWrite the story…"}
                  />
                ) : (
                  <div data-testid="a-preview" className="journal-prose text-base leading-relaxed text-ink/85 border border-border p-6 md:p-8 bg-white/40 min-h-[400px]" dangerouslySetInnerHTML={{ __html: renderMd(editing.body_md) }} />
                )}
              </div>

              <div className="mt-10 flex justify-end gap-3">
                <button onClick={close} className="lux-btn-outline py-3 px-5 text-[10px]">Cancel</button>
                <button onClick={save} disabled={saving} data-testid="save-article" className="lux-btn-dark py-3 px-6 text-[10px] flex items-center gap-2 disabled:opacity-60">
                  <Save size={13} strokeWidth={1.5} /> {saving ? "Saving…" : (editing.id ? "Save Changes" : "Publish Story")}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Media picker */}
      <Dialog open={showPicker} onOpenChange={setShowPicker}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-ivory border border-border rounded-none">
          <DialogHeader><DialogTitle className="font-serif text-3xl font-light tracking-tighter">Media Library</DialogTitle></DialogHeader>
          {media.length === 0 ? (
            <p className="text-sm text-muted-foreground mt-6">Library empty. <Link to="/admin/media" className="underline">Upload files</Link> first.</p>
          ) : (
            <ul className="mt-6 grid grid-cols-3 md:grid-cols-5 gap-4">
              {media.filter((m) => m.content_type?.startsWith("image/")).map((m) => {
                const url = `/api/media/${m.id}`;
                const active = editing?.cover === url || editing?.cover === abs(url);
                return (
                  <button key={m.id} type="button" onClick={() => { set("cover", url); setShowPicker(false); }}
                    data-testid={`pick-cover-${m.id}`}
                    className={`relative aspect-square overflow-hidden border transition-colors ${active ? "border-ink ring-2 ring-ink" : "border-border hover:border-ink"}`}>
                    <img src={`${API}/media/${m.id}`} alt="" loading="lazy" className="w-full h-full object-cover" />
                    {active && <span className="absolute top-2 right-2 w-6 h-6 bg-ink text-ivory flex items-center justify-center"><Check size={12} strokeWidth={1.5} /></span>}
                  </button>
                );
              })}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, testid, ...rest }) {
  return (
    <div>
      <label className="overline text-muted-foreground">{label}</label>
      <input className="lux-input" data-testid={testid} {...rest} />
    </div>
  );
}
